import { Injectable } from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {MapService} from '../map/map.service';
import {UtilsService} from '../utils/utils.service';
import {HttpClient} from '@angular/common/http';
import {} from '@types/googlemaps';

@Injectable()
export class RoutesService {

  private routePlaceManager: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public addPlace: Observable<any>;

  private routeAccountManager: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public addAccount: Observable<any>;

  private newRouteManager: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public confirmRoute: Observable<any>;

  route = {
    _id: undefined,
    name: undefined,
    date: undefined,
    startpoint: undefined,
    points: [],
    endpoint: undefined,
    optimized: false
  };

  legs: any;

  saved = true;

  constructor(private http: HttpClient, private mapService: MapService, private utilsService: UtilsService) {
    this.addPlace = this.routePlaceManager.asObservable();
    this.addAccount = this.routeAccountManager.asObservable();
    this.confirmRoute = this.newRouteManager.asObservable();
  }

  list() {
    return this.http.get('/api/routes');
  }

  get(routeId) {
    return this.http.get(`/api/routes/${routeId}`);
  }

  create(route) {
    return this.http.post('/api/routes', route);
  }

  delete(routeId) {
    return this.http.delete(`/api/routes/${routeId}`);
  }

  exportToPdf(route) {
    return this.http.post('/api/routes/exportToPdf', route);
  }

  exportToCalendar(route) {
    return this.http.post('/api/routes/exportToCalendar', route);
  }

  setStart(place) {
    if (place) {
      this.route.startpoint = {
        place: place,
        defaultTime: {
          hours: 9,
          minutes: 0
        },
        length: 0,
      };
    } else {
      this.route.startpoint = undefined;
    }
    this.getRoute();
    this.saved = false;
  }

  setEnd(place) {
    if (place) {
      this.route.endpoint = {
        place: place
      };
    } else {
      this.route.endpoint = undefined;
    }
    this.getRoute();
    this.saved = false;
  }

  setRoute(route) {
    this.saved = true;
    this.route._id = route._id;
    this.route.name = route.name;
    this.route.date = route.date;
    this.route.startpoint = route.startpoint;
    this.route.endpoint = route.endpoint;
    this.route.points = route.points.filter(p => p.place || p.account);
    this.route.optimized = route.optimized;
  }

  bulkAddAccounts(accounts) {
    if (accounts.length > 23) {
      accounts = accounts.slice(0, 23);
      alert('Can\'t add more than 23 points to one route');
    }
    accounts.forEach(a => {
      this.addAccountToRoute(a, false);
    });
    this.getRoute();
  }

  addPlaceToRoute(place, getRoute = true) {
    if (this.getAllPoints().length < 25) {
      const point =  {
        place: {
          name: place.name,
          address: place.formatted_address,
          placeId: place.place_id,
          coords: [place.geometry.location.lng(), place.geometry.location.lat()]
        },
        defaultTime: undefined,
        time: undefined,
        length: 30
      };
      if (!this.route.points.length && !this.route.startpoint) {
        point.defaultTime = {
          hours: 9,
          minutes: 0
        };
      } else {
        point.defaultTime = {
          hours: 0,
          minutes: 0
        };
      }
      this.route.points.push(point);
      if (getRoute) {
        this.getRoute();
      }
      this.saved = false;
    } else {
      alert('Can\'t add more points to one route');
    }
  }

  addAccountToRoute(account, getRoute = true) {
    if (this.getAllPoints().length < 25) {
      const point = {
        account: account,
        defaultTime: undefined,
        time: undefined,
        length: 30
      };
      if (!this.route.points.length && !this.route.startpoint) {
        point.defaultTime = {
          hours: 9,
          minutes: 0
        };
      } else {
        point.defaultTime = {
          hours: 0,
          minutes: 0
        };
      }
      this.route.points.push(point);
      if (getRoute) {
        this.getRoute();
      }
      this.saved = false;
    } else {
      alert('Can\'t add more points to one route');
    }

  }

  calculateTimes() {
    this.getAllPoints().forEach(point => {
      const index = this.getPointNumber(point);
      if (index === 0) {
        point.defaultTime = {
          hours: 9,
          minutes: 0
        };
        return;
      }
      if (!this.legs[index - 1]) {
        return;
      }
      const travelTime = this.legs[index - 1].duration.value;
      const prevPointTime = this.getAllPoints()[index - 1].time || this.getAllPoints()[index - 1].defaultTime;
      const prevPointLength = this.getAllPoints()[index - 1].length;
      const timeInMinutes = prevPointTime.hours * 60 + prevPointTime.minutes + prevPointLength + Math.round(travelTime / 60);
      point.defaultTime = this.utilsService.getTimeFromMinutes(timeInMinutes);
    });
  }

  clear() {

    this.route._id = undefined;
    this.route.name = undefined;
    this.route.date = undefined;
    this.route.startpoint = undefined;
    this.route.endpoint = undefined;
    this.route.points = [];
    this.route.optimized = undefined;

    this.clearLegs();

    this.mapService.clearRoute();

    this.saved = false;
  }

  clearLegs() {
    this.legs = undefined;
  }

  createNewRoute(accounts: any[]) {
    this.newRouteManager.next(accounts);
  }

  deletePoint(point) {
    this.route.points = this.route.points.filter(p => p !== point);
    this.getRoute();
  }

  getAllPoints() {
    const points = [...this.route.points];
    if (this.route.startpoint) {
      points.unshift(this.route.startpoint);
    }
    if (this.route.endpoint) {
      points.push(this.route.endpoint);
    }
    return points;
  }

  getNextPoint(point) {
    const index = this.getPointNumber(point);
    if (index === this.getAllPoints().length - 1) {
      return null;
    } else {
      return this.getAllPoints()[index + 1];
    }
  }

  getTimeDifference(point) {
    if (point.defaultTime && point.time) {
      return this.utilsService.getTimeDifferenceInMins(point.time, point.defaultTime);
    } else {
      return 0;
    }
  }

  getPointNumber(point) {
    const points = this.getAllPoints();
    return points.indexOf(point);
  }

  isLastPoint(point) {
    return this.getAllPoints().length === this.getPointNumber(point) + 1;
  }

  getAddress(point) {
    return point.place || point.account;
  }

  getRoute(optimize = false) {
    if (this.getAllPoints().length >= 2 && this.route.points.length) {
      this.mapService.getRoute(this.getRouteRequest(optimize))
        .subscribe(res => {
          this.route.optimized = optimize;
          this.legs = res.routes[0].legs;
          if (optimize) {
            const allPoints = this.getAllPoints();
            const waypoints = allPoints.slice(1, allPoints.length - 1);
            const orderedPoints = res.routes[0].waypoint_order.map(order => waypoints[order]);
            if (this.route.startpoint !== allPoints[0]) {
              orderedPoints.unshift(allPoints[0]);
            }
            if (this.route.endpoint !== allPoints[allPoints.length - 1]) {
              orderedPoints.push(allPoints[allPoints.length - 1]);
            }
            this.route.points = orderedPoints;
          }
          this.calculateTimes();
        });
    } else {
      this.legs = undefined;
      this.mapService.clearRoute();
    }
  }

  getRouteRequest(optimize) {
    const req = {
      origin: '',
      destination: '',
      waypoints: [],
      travelMode: 'DRIVING',
      optimizeWaypoints: optimize,
      provideRouteAlternatives: false
    };
    const points = this.getAllPoints();
    req.origin = <any>this.getCoords(points[0]);
    req.destination = <any>this.getCoords(points[points.length - 1]);
    req.waypoints = points.slice(1, points.length - 1).map(p => {
      return {
        location: this.getCoords(p),
        stopover: true
      };
    });

    return req;
  }

  getCoords(point) {
    return new google.maps.LatLng(this.getAddress(point).coords[1], this.getAddress(point).coords[0]);
  }

  getLetter(point) {
    const index = this.getPointNumber(point);
    const isLast = this.isLastPoint(point);
    const letter = String.fromCharCode(97 + index);
    return letter;
  }

  getLegTravelTime(point) {
    const index = this.getPointNumber(point);
    return this.legs && this.legs[index] && this.legs[index].duration.text;
  }

  getDayTotal() {
    return this.getTravelTime() + this.getMeetingTime();
  }

  getTravelTime() {
    if (this.legs && this.legs.length) {
      const val = this.legs.reduce((sum, l) => sum + l.duration.value, 0);
      return Math.floor(val / 60);
    } else {
      return 0;
    }
  }

  getDistance() {
    if (this.legs && this.legs.length) {
      const val = this.legs.reduce((sum, l) => sum + l.distance.value, 0);
      return (val * 0.000621371).toFixed(2);
    } else {
      return 0;
    }
  }

  getMeetingTime() {
    if (this.route.points && this.route.points.length) {
      return this.route.points.reduce((sum, p) => sum + p.length, 0);
    } else {
      return 0;
    }
  }

  isPlaceInRoute(place) {
    return this.route.points.find(p => {
      return p.place && p.place.placeId === place.place_id;
    });
  }

  isAccountInRoute(account) {
    return this.route.points.find(p => {
      return p.account && p.account._id === account._id;
    });
  }



}



// WEBPACK FOOTER //
// ./client/app/core/routes/routes.service.ts